import {
  orderReadinessUrl,
  orderReportingModeUrl,
  reportingClientsUrl,
  reportingProjectsUrl,
} from 'router/url';
import { fetchWithAuthorization } from 'utils/authorizedFetch';
import {
  ApiResult,
  AvailabilityState,
  ReportingClient,
  ReportingMode,
  ReportingProject,
} from './types';
import { classifyAvailability } from './utils';

const availabilityRequests = new Map<string, Promise<AvailabilityState>>();

const readResponse = async <T>(response: Response): Promise<ApiResult<T>> => {
  const text = await response.text();
  let data: T | undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  return { ok: response.ok, status: response.status, data };
};

const safeGet = async <T>(url: string, signal?: AbortSignal) => {
  try {
    return await readResponse<T>(await fetchWithAuthorization(url, { signal }));
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      throw error;
    }
    return { ok: false, status: 0 } as ApiResult<T>;
  }
};

// Availability is read for the owner of the timesheet being edited (change
// request of 26.10, P5): a leader or DevHR editing someone else's timesheet
// gets that person's assignments. Without a username the backend uses the
// signed-in account.
const availabilityQuery = (date: string, username?: string) => {
  const params = new URLSearchParams({ date });
  if (username) {
    params.set('username', username);
  }
  return params.toString();
};

export const fetchReportingClients = (
  date: string,
  username?: string,
  signal?: AbortSignal,
) =>
  safeGet<ReportingClient[]>(
    `${reportingClientsUrl}?${availabilityQuery(date, username)}`,
    signal,
  );

export const fetchReportingProjects = (
  date: string,
  username?: string,
  signal?: AbortSignal,
) =>
  safeGet<ReportingProject[]>(
    `${reportingProjectsUrl}?${availabilityQuery(date, username)}`,
    signal,
  );

export const fetchReportingAvailability = (date: string, username?: string) => {
  // One request per account and date: the same date of two timesheets is
  // two different answers.
  const key = `${username || ''}|${date}`;
  const existing = availabilityRequests.get(key);
  if (existing) {
    return existing;
  }

  const request = Promise.all([
    fetchReportingClients(date, username),
    fetchReportingProjects(date, username),
  ]).then(([clients, projects]) => ({
    status: classifyAvailability(clients, projects),
    clients: clients.data || [],
    projects: projects.data || [],
  }));
  availabilityRequests.set(key, request);
  const clearRequest = () => {
    if (availabilityRequests.get(key) === request) {
      availabilityRequests.delete(key);
    }
  };
  request.then(clearRequest, clearRequest);
  return request;
};

// Without a date the backend picks the go-live date (or today once it passed).
export const fetchOrderReadiness = (
  { date, includeInternal }: { date?: string; includeInternal: boolean },
  signal?: AbortSignal,
) => {
  const params = new URLSearchParams();
  if (date) {
    params.set('date', date);
  }
  params.set('includeInternal', String(includeInternal));
  return safeGet<unknown>(`${orderReadinessUrl}?${params.toString()}`, signal);
};

export const checkReportingRange = async (
  dates: string[],
  clientId: number,
  projectId: number,
  username?: string,
) => {
  const results = await Promise.all(
    dates.map(async (date) => {
      const projects = await fetchReportingProjects(date, username);
      return {
        date,
        ok: projects.ok,
        unauthorized: projects.status === 401 || projects.status === 403,
        covered:
          projects.data?.some(
            ({ id, clientId: projectClientId }) =>
              id === projectId && projectClientId === clientId,
          ) === true,
      };
    }),
  );
  return {
    unavailable: results
      .filter(({ ok, covered }) => ok && !covered)
      .map(({ date }) => date),
    failed: results.filter(({ ok }) => !ok).length,
    unauthorized: results.some(({ unauthorized }) => unauthorized),
  };
};

const REPORTING_MODES = ['OFF', 'WARNING', 'ENFORCING'];
let reportingModeRequest: Promise<ReportingMode | null> | null = null;

// The mode changes rarely, so one request serves the whole session. A failed
// or unrecognised response resolves to null (mode unknown) and is retried on
// the next call.
export const fetchReportingMode = () => {
  if (!reportingModeRequest) {
    const request = safeGet<ReportingMode>(orderReportingModeUrl).then(
      (result) => {
        const mode = result.data?.mode;
        if (result.ok && mode && REPORTING_MODES.includes(mode)) {
          return { mode, effectiveDate: result.data?.effectiveDate || null };
        }
        return null;
      },
    );
    reportingModeRequest = request;
    request.then(
      (mode) => {
        if (!mode && reportingModeRequest === request) {
          reportingModeRequest = null;
        }
      },
      () => {
        reportingModeRequest = null;
      },
    );
  }
  return reportingModeRequest;
};

export const resetReportingModeCache = () => {
  reportingModeRequest = null;
};
